'use client';

/**
 * useRailFlyouts - which rail group's flyout is open, and why (Q-1170).
 *
 * One flyout at a time. Keyboard focus outranks the pointer: an OPEN flyout
 * whose group holds KEYBOARD focus is not closed by the pointer leaving it, nor
 * taken over by the pointer crossing another icon. Once that flyout closes
 * (Escape, a route change) the hold ends and hover works for every group. Focus left behind by a mouse click is not
 * keyboard focus, so it holds nothing. Moving from one open flyout to another
 * icon waits RAIL_HOVER_INTENT_MS, so a diagonal path into a flyout that crosses
 * the next icon does not switch groups (menu aim). Leaving the rail for the page
 * closes at once: a lingering flyout was hover bug #12 before 40513e82d.
 */

import React from 'react';

/** How long the pointer may rest elsewhere in the rail (another icon, a divider) before the open flyout gives way. */
export const RAIL_HOVER_INTENT_MS = 150;

export interface RailFlyouts {
  openKey: string | null;
  /** Open now (a tap, a key): cancels any pending hover change. */
  open: (key: string) => void;
  /** Close now if `key` is the open group. */
  close: (key: string) => void;
  pointerEnter: (key: string) => void;
  /** `withinRail`: the pointer moved to another part of the rail (not out to the page). */
  pointerLeave: (key: string, withinRail: boolean) => void;
  /** Focus entered the group's wrapper. Records whether it came from the keyboard. */
  focusEnter: (key: string, wrapper: HTMLElement) => void;
  /** Focus left the group's wrapper. */
  focusLeave: (key: string) => void;
}

export function useRailFlyouts(resetKey: unknown): RailFlyouts {
  const [openKey, setOpenKey] = React.useState<string | null>(null);
  const openKeyRef = React.useRef<string | null>(null);
  const lastInputKeyboardRef = React.useRef(false);
  const keyboardFocusRef = React.useRef<{ key: string; wrapper: HTMLElement } | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const setOpen = React.useCallback((key: string | null) => {
    openKeyRef.current = key;
    setOpenKey(key);
  }, []);
  const cancelPending = React.useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);
  const afterIntent = React.useCallback((change: () => void) => {
    cancelPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      change();
    }, RAIL_HOVER_INTENT_MS);
  }, [cancelPending]);
  // The open group whose wrapper still holds keyboard focus (focus can vanish with an unmounted flyout without a blur).
  const keyboardHeldKey = React.useCallback(() => {
    const held = keyboardFocusRef.current;
    return held && held.key === openKeyRef.current && held.wrapper.contains(document.activeElement) ? held.key : null;
  }, []);

  // Input modality: a key press makes the next focus keyboard focus; a pointer press makes it pointer focus.
  React.useEffect(() => {
    const onKey = () => {
      lastInputKeyboardRef.current = true;
    };
    const onPointer = () => {
      lastInputKeyboardRef.current = false;
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('mousedown', onPointer, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('mousedown', onPointer, true);
      cancelPending();
    };
  }, [cancelPending]);

  // A route change closes the flyout and ends any keyboard hold (focus left on an icon after navigating is stale).
  React.useEffect(() => {
    cancelPending();
    keyboardFocusRef.current = null;
    setOpen(null);
  }, [resetKey, cancelPending, setOpen]);

  const open = React.useCallback((key: string) => {
    cancelPending();
    setOpen(key);
  }, [cancelPending, setOpen]);

  const close = React.useCallback((key: string) => {
    cancelPending();
    if (openKeyRef.current === key) setOpen(null);
  }, [cancelPending, setOpen]);

  const pointerEnter = React.useCallback((key: string) => {
    cancelPending();
    const held = keyboardHeldKey();
    if (held !== null && held !== key) return;
    if (openKeyRef.current === key) return;
    if (openKeyRef.current === null) setOpen(key);
    else afterIntent(() => setOpen(key));
  }, [cancelPending, keyboardHeldKey, afterIntent, setOpen]);

  const pointerLeave = React.useCallback((key: string, withinRail: boolean) => {
    if (keyboardHeldKey() === key) return;
    const closeUnlessHeld = () => {
      if (keyboardHeldKey() === null) setOpen(null);
    };
    if (withinRail) afterIntent(closeUnlessHeld);
    else {
      cancelPending();
      closeUnlessHeld();
    }
  }, [keyboardHeldKey, afterIntent, cancelPending, setOpen]);

  const focusEnter = React.useCallback((key: string, wrapper: HTMLElement) => {
    keyboardFocusRef.current = lastInputKeyboardRef.current ? { key, wrapper } : null;
    open(key);
  }, [open]);

  const focusLeave = React.useCallback((key: string) => {
    if (keyboardFocusRef.current?.key === key) keyboardFocusRef.current = null;
    close(key);
  }, [close]);

  return React.useMemo(
    () => ({ openKey, open, close, pointerEnter, pointerLeave, focusEnter, focusLeave }),
    [openKey, open, close, pointerEnter, pointerLeave, focusEnter, focusLeave],
  );
}
